import routes from '@/config/routes';
import { v3Get, v3Post } from '@/api/axios.instance.V3';
import type { ApiResponse } from '../types';
import type {
  IAccountForm,
  ICredentialForm,
  ISWSOrderForm,
  ISetCredentialForm,
  IUpdateGigPhase,
  IUpdateSWSOrderForm,
  IUpdateCredentialForm,
  IESignDocumentsRequest,
  IMSAForm,
  IUpdateMSA,
  ISOWCreateForm,
  ISOWUpdateForm,
  IExistingGroup,
  INewGroup,
  IBackgroundStatusForm,
} from '@/store/admin/types/types';

interface IAgentSearch {
  text: string;
  pageNum: number;
  pageSize: number;
}

const getServiceproviderList = (payload: IAgentSearch, loginType: string): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.getServiceProviders}/${loginType || 'canLogin'}`, payload);

const getESignMSADocuments = (payload: IESignDocumentsRequest): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.getESignDocuments}`, payload);

const getESignSOWDocuments = (payload: IESignDocumentsRequest): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.getESignDocuments}`, payload);

const getESignVersionsDocuments = (eDocId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getESignVersions}${eDocId}`);

const getESignDocumentConfig = (): ApiResponse => v3Get(`${routes.backendUrl.agent.getESignDocumentConfig}`);

const getGigCountries = (gigId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getGigCountries}${gigId}/list`);

const getESignTemplateList = (): ApiResponse => v3Get(`${routes.backendUrl.agent.getTemplateList}`);

const getDocument = (edocId: number, versionNo: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getESignDocument}/${edocId || 0}/${versionNo || 0}`);

const getESignDocumentContent = (templateId: string): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getESignDocumentContent}/${templateId || 0}/base64`);

const getTemplateList = (): ApiResponse => v3Get(`${routes.backendUrl.agent.getTemplateList}`);

const createMSADocument = (payload: IMSAForm): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.createMSADocument}`, payload);

const updateMSADocument = (payload: IUpdateMSA): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.updateMSADocument}`, payload);

const createSOWDocument = (payload: ISOWCreateForm): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.createSOWDocument}`, payload);

const updateSOWDocument = (payload: ISOWUpdateForm): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.updateSOWDocument}`, payload);

const getServiceProviderDetails = (userId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getServiceProviderDetails}/${userId || 0}`);

const getServiceProviderGigsDetails = (userId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getServiceProviderGigsDetails}/${userId || 0}`);

const getServiceProviderApplicationHistoryDetails = (userId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getServiceProviderApplicationHistoryDetails}/${userId || 0}`);

const createAgent = (payload: IAccountForm): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.createAgent}`, { ...payload, userId: -1 });

const updateAgent = (payload: IAccountForm): ApiResponse => v3Post(`${routes.backendUrl.agent.updateAgent}`, payload);

const getServiceProviderCredDetails = (userId: number, currentCredential: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getServiceProviderCredDetails}/${userId || 0}/${currentCredential || 0}`);

const getServiceProviderTPCredDetails = (userId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getServiceProviderTPCredDetails}/${userId || 0}`);

const getServiceProviderSpeedTest = (userId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getAgentSpeedTest}/${userId}`);

const getSpeedTestDetail = (testId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getAgentSpeedTestDetail}/${testId}`);

const getServiceProviderHardwareTest = (userId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getAgentHardwareTest}/${userId}`);

const getServiceProviderSupplierList = (): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getServiceProviderSupplierList}`);

const createCredential = (payload: ICredentialForm): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.createCredential}`, payload);

const getCredentialList = (): ApiResponse => v3Get(`${routes.backendUrl.agent.getCredentialList}`);

const getSWSReasonList = (): ApiResponse => v3Get(`${routes.backendUrl.agent.getSWSReasonList}`);

const getSWSVersionList = (): ApiResponse => v3Get(`${routes.backendUrl.agent.getSWSVersionList}`);

const getSWSStatusList = (): ApiResponse => v3Get(`${routes.backendUrl.agent.getSWSStatusList}`);

const getVoiceAnswerList = (applicationId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getVoiceQuestionAnswerList}/${applicationId}`);

const createSWSOrder = (payload: ISWSOrderForm): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.createSWSOrder}`, payload);

const getSWSOrderDetails = (userId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getSWSOrderDetails}/${userId || 0}`);

const setAgentCredential = (userId: number, payload: ISetCredentialForm): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.setAgentCredential}/${userId}`, payload);

const updateAgentCredential = (userId: number, payload: IUpdateCredentialForm): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.updateAgentCredential}/${userId}`, payload);

const updateSWSDetails = (payload: IUpdateSWSOrderForm): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.updateSWSDetails}`, payload);

const getServiceProviderGigPhaseList = (): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getServiceProviderGigPhaseList}`);

const updateGigPhase = (payload: IUpdateGigPhase): ApiResponse =>
  v3Post(`${routes.backendUrl.agent.updateGigPhase}/${payload.userId}/${payload.gigId}/${payload.gigPhaseId}`);

const getResetPasswordLink = (userId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getResetPasswordLink}/${userId || 0}`);

const getDuplicateDetailsList = (userId: number, fraudCheckTypeId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getDulpicateList}/${userId || 0}/${fraudCheckTypeId}`);

const getFraudCheckGroupUser = (userId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getFraudGroupUser}/${userId || 0}`);

const updateExistingGroup = (payload: IExistingGroup): ApiResponse =>
  v3Post(routes.backendUrl.agent.updateExistingGroup, payload);

const createNewGroup = (payload: INewGroup): ApiResponse => v3Post(routes.backendUrl.agent.createNewGroup, payload);

const getMobileVerificationTypesByCountry = (countryId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getMobileVerificationTypesByCountry}?countryId=${countryId}`);

const getMobileVerificationTypes = (): ApiResponse => v3Get(`${routes.backendUrl.agent.getMobileVerificationTypes}`);

const getBGCheckUserData = (userId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.agent.getBGCheckUserData}/${userId || 0}`);

const saveBGCheckUserData = (payload: IBackgroundStatusForm): ApiResponse =>
  v3Post(routes.backendUrl.agent.getBGCheckUserData, payload);

const getBGCheckOptions = (): ApiResponse => v3Get(`${routes.backendUrl.agent.getBGCheckOptions}`);

export const agentAdminApi = {
  getServiceproviderList,
  getServiceProviderDetails,
  getServiceProviderGigsDetails,
  getServiceProviderApplicationHistoryDetails,
  createAgent,
  updateAgent,
  getServiceProviderCredDetails,
  getServiceProviderTPCredDetails,
  getServiceProviderSpeedTest,
  getSpeedTestDetail,
  getServiceProviderHardwareTest,
  getServiceProviderSupplierList,
  createCredential,
  getCredentialList,
  getVoiceAnswerList,
  getSWSReasonList,
  getSWSVersionList,
  createSWSOrder,
  getSWSOrderDetails,
  setAgentCredential,
  updateAgentCredential,
  updateSWSDetails,
  getServiceProviderGigPhaseList,
  updateGigPhase,
  getSWSStatusList,
  getResetPasswordLink,
  getESignMSADocuments,
  getESignSOWDocuments,
  getESignVersionsDocuments,
  getESignDocumentConfig,
  getGigCountries,
  getESignTemplateList,
  getTemplateList,
  getDocument,
  getESignDocumentContent,
  createMSADocument,
  updateMSADocument,
  createSOWDocument,
  updateSOWDocument,
  getDuplicateDetailsList,
  getFraudCheckGroupUser,
  updateExistingGroup,
  createNewGroup,
  getMobileVerificationTypesByCountry,
  getMobileVerificationTypes,
  getBGCheckUserData,
  saveBGCheckUserData,
  getBGCheckOptions,
};
